import { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, X } from "lucide-react";
import { inputCls, btnPrimary, btnSecondary } from "./crm";
import { errMsg } from "./useCrm";

/** Password form. `forced` = first-login screen (full page, cannot be dismissed). Otherwise a modal. */
export default function ChangePasswordView({ onSubmit, onClose, onLogout, forced = false, userName }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("The two passwords do not match.");
    setBusy(true);
    try {
      await onSubmit(current, next);
      onClose?.();
    } catch (err) {
      setError(errMsg(err, "Could not update password."));
    } finally {
      setBusy(false);
    }
  };

  const form = (
    <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-slate-700">{forced ? "Temporary access code" : "Current password"}</label>
        <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" className={inputCls} />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-slate-700">New password</label>
        <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="cp-new" className={inputCls} placeholder="At least 8 characters" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-semibold text-slate-700">Confirm new password</label>
        <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" className={inputCls} />
      </div>
      {error && <p data-testid="cp-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" disabled={busy} data-testid="cp-submit" className={`${btnPrimary} flex-1 py-3`}>{busy ? "Saving…" : forced ? "Set password & continue" : "Update password"}</button>
        {!forced && <button type="button" onClick={onClose} data-testid="cp-cancel" className={btnSecondary}>Cancel</button>}
      </div>
    </form>
  );

  if (forced) {
    return (
      <div className="min-h-screen bg-[#f4f6fa] flex items-center justify-center px-5 py-12" data-testid="force-change-password" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0b1528] text-white"><KeyRound className="h-5 w-5" /></span>
          <h1 className="mt-6 text-2xl font-extrabold text-slate-900">Welcome{userName ? `, ${userName.split(" ")[0]}` : ""}. Set your password.</h1>
          <p className="mt-1.5 text-sm text-slate-500">Your temporary access code only works once. Choose a password you'll use from now on.</p>
          <div className="mt-7">{form}</div>
          <button onClick={onLogout} data-testid="cp-signout" className="mt-5 text-xs font-semibold text-slate-500 hover:text-slate-800">Sign out instead</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-slate-900/40 sm:px-4" onClick={onClose} data-testid="change-password-modal">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-t-2xl sm:rounded-2xl border border-slate-200 bg-white p-6 pb-safe shadow-2xl" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-slate-900">Change password</h2>
            <p className="mt-0.5 text-xs text-slate-500">You'll stay signed in on this device.</p>
          </div>
          <button onClick={onClose} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-5">{form}</div>
      </motion.div>
    </div>
  );
}
